import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Search, Trash2, Ban, BadgeCheck, Users, Briefcase, BarChart3, Mail, Send, ShieldOff, Shield, X, Bell } from "lucide-react";
import VerifiedBadge from "../components/VerifiedBadge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "blast", label: "Email blast", icon: Mail },
  { id: "notify", label: "Notify users", icon: Bell },
];
export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="admin-page">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">Admin panel</h1>
      <p className="text-sm text-muted-foreground mt-1">Manage users, jobs, analytics and announcements.</p>

      <nav className="mt-6 border-b border-border flex gap-1 overflow-x-auto scrollbar-hide" data-testid="admin-tabs">
        {TABS.map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm whitespace-nowrap inline-flex items-center gap-2 border-b-2 transition ${tab===t.id?"border-brand text-brand font-semibold":"border-transparent text-muted-foreground hover:text-foreground"}`}>
              <I className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-8">
        {tab === "users" && <UsersTab />}
        {tab === "jobs" && <JobsTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "blast" && <BlastTab />}
        {tab === "notify" && <NotifyTab />}
      </div>
    </div>
  );
}

/* -------------------- USERS -------------------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (q && !(u.email?.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase()) || u.username?.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [users, q, roleFilter]);

  const update = async (id, payload) => {
    await api.patch(`/admin/users/${id}`, payload);
    toast.success("User updated"); load();
  };
  const remove = async (id, label) => {
    if (!window.confirm(`Delete user "${label}"? This is permanent.`)) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User deleted"); load();
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input className="input-base pl-9" placeholder="Search name, email, @username" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-user-search" />
        </div>
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          {["all","worker","manager","freelancer","admin"].map(r => (
            <button key={r} onClick={() => setRoleFilter(r)} className={`chip ${roleFilter===r?"chip-active":""}`} data-testid={`admin-role-${r}`}>{r}</button>
          ))}
        </div>
        <span className="ml-auto text-xs text-muted-foreground">{filtered.length} users</span>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {filtered.map(u => <UserCard key={u.id} u={u} update={update} remove={remove} />)}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider"><tr>
            <th className="text-left p-3">User</th><th className="text-left p-3">Email</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Role</th>
            <th className="p-3">Verified</th><th className="p-3">Approved</th><th className="p-3">Status</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-t border-border">
                <td className="p-3">
                  <p className="font-medium flex items-center gap-1">{u.name} {u.verified && <VerifiedBadge size={12} />}</p>
                  <p className="text-xs text-muted-foreground">@{u.username}</p>
                </td>
                <td className="p-3 text-xs">{u.email}</td>
                <td className="p-3 text-xs">{u.phone || "—"}</td>
                <td className="p-3 capitalize">{u.role}</td>
                <td className="p-3 text-center">{u.verified ? "✓" : "—"}</td>
                <td className="p-3 text-center">{u.approved ? "✓" : "—"}</td>
                <td className="p-3 text-center">{u.blocked ? <span className="text-red-600 font-medium">Blocked</span> : <span className="text-emerald-600">Active</span>}</td>
                <td className="p-3"><RowActions u={u} update={update} remove={remove} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserCard({ u, update, remove }) {
  return (
    <div className="card-flat" data-testid={`admin-user-${u.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium flex items-center gap-1">{u.name} {u.verified && <VerifiedBadge size={12} />}</p>
          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          <span className="label-eyebrow text-brand">{u.role}</span>
          {u.blocked && <span className="ml-2 text-xs text-red-600">Blocked</span>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        <RowActions u={u} update={update} remove={remove} />
      </div>
    </div>
  );
}

function RowActions({ u, update, remove }) {
  return (
    <div className="flex flex-wrap gap-1 items-center">
      <select
        value={u.role}
        onChange={(e) => {
          if (window.confirm(`Change ${u.name}'s role from "${u.role}" to "${e.target.value}"?`)) {
            update(u.id, { role: e.target.value });
          }
        }}
        className="chip text-xs !py-1 cursor-pointer"
        data-testid={`role-select-${u.id}`}
      >
        <option value="worker">worker</option>
        <option value="manager">manager</option>
        <option value="freelancer">freelancer</option>
        <option value="admin">admin</option>
      </select>
      <button onClick={() => update(u.id, { verified: !u.verified })} className="chip text-xs" data-testid={`btn-verify-${u.id}`}>
        <BadgeCheck className="w-3 h-3" /> {u.verified ? "Unverify" : "Verify"}
      </button>
      <button onClick={() => update(u.id, { blocked: !u.blocked })} className={`chip text-xs ${u.blocked ? "chip-active" : ""}`} data-testid={`btn-block-${u.id}`}>
        {u.blocked ? <Shield className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
        {u.blocked ? "Unblock" : "Block"}
      </button>
      <button onClick={() => remove(u.id, u.email)} className="chip text-xs text-red-600 hover:!bg-red-50 hover:!border-red-300 hover:!text-red-700" data-testid={`btn-delete-${u.id}`}>
        <Trash2 className="w-3 h-3" /> Delete
      </button>
    </div>
  );
}

/* -------------------- JOBS -------------------- */
function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");

  const load = async () => { const { data } = await api.get("/admin/jobs"); setJobs(data); setSelected(new Set()); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return jobs;
    const t = q.toLowerCase();
    return jobs.filter(j => j.title?.toLowerCase().includes(t) || j.specialization?.toLowerCase().includes(t) || j.posted_by_name?.toLowerCase().includes(t));
  }, [jobs, q]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(s => s.size === filtered.length ? new Set() : new Set(filtered.map(j => j.id)));

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} job(s)? This is permanent.`)) return;
    await api.post("/admin/jobs/bulk-delete", { ids: [...selected] });
    toast.success(`Deleted ${selected.size} job(s)`); load();
  };

  return (
    <div data-testid="admin-jobs-tab">
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input className="input-base pl-9" placeholder="Search title, spec, poster" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-job-search" />
        </div>
        <button onClick={toggleAll} className="chip">{selected.size === filtered.length && filtered.length > 0 ? "Deselect all" : "Select all"}</button>
        <button onClick={bulkDelete} disabled={!selected.size} className={`btn-primary !py-2 !px-4 ${!selected.size?"opacity-50 cursor-not-allowed":""}`} data-testid="bulk-delete-jobs">
          <Trash2 className="w-4 h-4" /> Delete selected ({selected.size})
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(j => (
          <div key={j.id} className={`card-flat relative ${selected.has(j.id)?"!border-brand !bg-brand-tint/30":""}`} data-testid={`admin-job-${j.id}`}>
            <label className="absolute top-3 right-3 cursor-pointer">
              <input type="checkbox" checked={selected.has(j.id)} onChange={() => toggle(j.id)} className="w-4 h-4 accent-purple-600" data-testid={`select-job-${j.id}`} />
            </label>
            <span className="label-eyebrow text-muted-foreground">{j.opportunity_type}</span>
            <p className="mt-1 font-display text-lg pr-6">{j.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{j.specialization} · {j.status} · ₹{j.payment}</p>
            <p className="text-xs text-muted-foreground mt-0.5">by {j.posted_by_name}</p>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No jobs found.</p>}
      </div>
    </div>
  );
}

/* -------------------- ANALYTICS -------------------- */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics?days=30").then(r => setData(r.data));
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const stats = [
    { k: "Total users", v: data.totals.users },
    { k: "Verified", v: data.totals.verified_users },
    { k: "Blocked", v: data.totals.blocked_users },
    { k: "Total jobs", v: data.totals.jobs },
    { k: "Active jobs", v: data.totals.active_jobs },
    { k: "Applications", v: data.totals.applications },
    { k: "Freelancers", v: data.totals.freelancers },
  ];

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {stats.map(s => (
          <div key={s.k} className="card-flat !p-4">
            <p className="font-display text-2xl tabular-nums">{s.v}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{s.k}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RoleBox label="Workers" v={data.by_role.worker} />
        <RoleBox label="Managers" v={data.by_role.manager} />
        <RoleBox label="Freelancers" v={data.by_role.freelancer} />
      </div>

      <div className="card-flat">
        <p className="label-eyebrow text-muted-foreground">Daily signups (last 30d)</p>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily_signups}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#9333ea" strokeWidth={2.5} dot={{ r: 3, fill: "#9333ea" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-flat">
        <p className="label-eyebrow text-muted-foreground">Daily job posts (last 30d)</p>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.daily_jobs}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="count" fill="#9333ea" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-flat">
        <p className="label-eyebrow text-muted-foreground">Top specializations</p>
        <div className="mt-4 space-y-2">
          {data.top_specializations.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet.</p>}
          {data.top_specializations.map((s, i) => (
            <div key={s.specialization} className="flex items-center gap-3">
              <span className="font-mono text-xs text-muted-foreground w-6">#{i+1}</span>
              <span className="flex-1 text-sm">{s.specialization}</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-brand" style={{ width: `${(s.count / data.top_specializations[0].count) * 100}%` }} />
              </div>
              <span className="font-mono text-xs tabular-nums w-8 text-right">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RoleBox({ label, v }) {
  return (
    <div className="card-flat !p-4">
      <p className="label-eyebrow text-muted-foreground">{label}</p>
      <p className="font-display text-3xl mt-1 tabular-nums">{v}</p>
    </div>
  );
}

/* -------------------- EMAIL BLAST -------------------- */
function BlastTab() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [role, setRole] = useState("all");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this email to all ${role==="all"?"users":role+"s"}? This cannot be undone.`)) return;
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/admin/email-blast", {
        subject,
        body_html: `<p>${body.replace(/\n/g, "<br/>")}</p>`,
        role,
      });
      setResult(data);
      toast.success(`Sent to ${data.sent} of ${data.queued}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5" data-testid="admin-blast-tab">
      <div>
        <label className="label-eyebrow text-muted-foreground">Recipients</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {[["all","Everyone"],["worker","Workers only"],["manager","Managers only"],["freelancer","Freelancers only"]].map(([v,l]) => (
            <button type="button" key={v} onClick={() => setRole(v)} className={`chip ${role===v?"chip-active":""}`} data-testid={`blast-role-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Subject</label>
        <input className="input-base mt-2" required value={subject} onChange={e => setSubject(e.target.value)} placeholder="A new feature has launched on EarnMate…" data-testid="blast-subject" />
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Message</label>
        <textarea className="input-base mt-2 !rounded-2xl" rows={8} required value={body} onChange={e => setBody(e.target.value)} placeholder="Write your announcement here. Plain text — line breaks are preserved." data-testid="blast-body" />
        <p className="mt-2 text-xs text-muted-foreground">Goes out via Resend with the EarnMate purple-branded template + your logo header.</p>
      </div>
      <button disabled={sending} className="btn-primary" data-testid="blast-send"><Send className="w-4 h-4" /> {sending ? "Sending…" : `Send to ${role==="all"?"everyone":role+"s"}`}</button>

      {result && (
        <div className="card-flat !bg-brand-tint/40">
          <p className="font-medium">Done.</p>
          <p className="text-sm text-muted-foreground mt-1">Queued {result.queued} · Sent {result.sent} · Failed {result.failed}</p>
        </div>
      )}
    </form>
  );
}

/* -------------------- NOTIFY BLAST -------------------- */
function NotifyTab() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [role, setRole] = useState("all");
  const [link, setLink] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this notification to all ${role === "all" ? "users" : role + "s"}?`)) return;
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/admin/notify-blast", { title, message, role, link });
      setResult(data);
      toast.success(`Sent to ${data.sent} users`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5">
      <div>
        <label className="label-eyebrow text-muted-foreground">Recipients</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {[["all","Everyone"],["worker","Workers only"],["manager","Managers only"],["freelancer","Freelancers only"]].map(([v,l]) => (
            <button type="button" key={v} onClick={() => setRole(v)} className={`chip ${role===v?"chip-active":""}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Title</label>
        <input className="input-base mt-2" required value={title} onChange={e => setTitle(e.target.value)} placeholder="New feature available!" />
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Message</label>
        <textarea className="input-base mt-2 !rounded-2xl" rows={4} required value={message} onChange={e => setMessage(e.target.value)} placeholder="Write your announcement here…" />
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Link (optional)</label>
        <input className="input-base mt-2" value={link} onChange={e => setLink(e.target.value)} placeholder="/jobs or /dashboard" />
        <p className="text-xs text-muted-foreground mt-1">Users will be taken here when they tap the notification.</p>
      </div>
      <button disabled={sending} className="btn-primary">
        <Bell className="w-4 h-4" /> {sending ? "Sending…" : `Notify ${role === "all" ? "everyone" : role + "s"}`}
      </button>
      {result && (
        <div className="card-flat !bg-brand-tint/40">
          <p className="font-medium">Done.</p>
          <p className="text-sm text-muted-foreground mt-1">Sent to {result.sent} users</p>
        </div>
      )}
    </form>
  );
}